import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import { ClipLoader } from 'react-spinners';
import { Download, ArrowLeft, ZoomIn, ZoomOut } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import ResumePrint from './ResumePrint';
import { getResumeJsonData } from '../../api/resume';

const DEFAULT_SECTION_ORDER = ['summary', 'skills', 'experience', 'projects', 'education'];

const ResumePrintPage = () => {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [data, setData] = useState(null);
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fontSize, setFontSize] = useState(13); // Default base font size (≈ 9.75pt in print)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getResumeJsonData(resumeId);
        if (result && result.success) {
          setData(result.resp);
          if (result.resp.sectionOrder && Array.isArray(result.resp.sectionOrder)) {
            setSectionOrder(result.resp.sectionOrder);
          }
        } else {
          setError(result?.resp || 'Failed to load resume data.');
        }
      } catch (err) {
        setError('Failed to load resume data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [resumeId]);

  const handlePrint = useReactToPrint({
    contentRef,
    documentTitle: data?.personalInfo?.name
      ? `${data.personalInfo.name} - Resume`
      : 'Resume',
  });

  const onDownload = useCallback(() => {
    handlePrint();
  }, [handlePrint]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-600">
        <ClipLoader color="#4f46e5" size={40} />
        <p className="mt-4 font-medium">Loading resume…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-red-600">
        <div className="bg-red-50 p-6 rounded-lg border border-red-200 text-center max-w-md">
          <h3 className="text-lg font-bold mb-2">Error Loading Resume</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="bg-gray-100 min-h-screen py-10 px-2 sm:px-6">
      {/* Toolbar — hidden when printing */}
      <div className="resume-print-toolbar max-w-4xl mx-auto mb-4 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
        <button
          onClick={() => navigate(`/myresumes/${resumeId}`)}
          className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors self-start sm:self-auto whitespace-nowrap"
        >
          <ArrowLeft size={18} />
          Back to Editor
        </button>
        
        <div className="flex bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden w-full sm:w-auto justify-center">
          <button 
            onClick={() => setFontSize(Math.max(9, fontSize - 1))}
            className="px-3 py-2 text-gray-600 hover:bg-gray-50 border-r border-gray-300 transition-colors"
            title="Decrease font size"
          >
            <ZoomOut size={16} />
          </button>
          <div className="px-3 py-2 text-sm font-medium text-gray-700 flex items-center bg-gray-50 border-r border-gray-300" title={`Print size ≈ ${(fontSize / 1.333).toFixed(1)}pt`}>
            {fontSize}px
          </div>
          <button 
            onClick={() => setFontSize(Math.min(16, fontSize + 1))}
            className="px-3 py-2 text-gray-600 hover:bg-gray-50 border-r border-gray-300 transition-colors"
            title="Increase font size"
          >
            <ZoomIn size={16} />
          </button>
          <button
            onClick={onDownload}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 whitespace-nowrap"
          >
            <Download size={18} />
            Download PDF
          </button>
        </div>
      </div>

      {/* Resume — the ref target for react-to-print */}
      <div className="flex justify-center pb-20">
        <ResumePrint ref={contentRef} data={data} sectionOrder={sectionOrder} fontSize={fontSize} />
      </div>
    </div>
  );
};

export default ResumePrintPage;
